import { ReadonlyMat3, ReadonlyVec2, ReadonlyVec3, vec2 } from 'gl-matrix';
import {
    composeFrameRotation,
    rotationAboutViewNormal,
    signedAngleAboutPivotRad,
} from '~/domain/viewport/frameRotation';
import { TextureView } from '~/engine/view/TextureView';
import { PlaneView } from '~/engine/view/views/PlaneView';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../EventHandlerResult';
import { ModifierKeys } from '../ModifierKeys';
import { Tool } from '../Tool';

/**
 * Below this distance (canvas px) from the pivot, a press does not start a
 * rotation: the angular mapping is degenerate at the centre (an
 * infinitesimal pointer move there sweeps an arbitrarily large bearing).
 */
const ROTATE_DEAD_ZONE_PX = 12;

/**
 * {@link RotateViewTool} lets the user drag on any {@link PlaneView} to
 * rotate the oblique frame that view shares with the other views of its
 * Environment.
 *
 * Left-click + drag rotates about the view's own normal (its viewing
 * direction) — a "grab and spin" about the plane intersection point,
 * classic MPR crosshair-spin semantics: dragging in one view spins the
 * anatomy within that view's plane, while the other views swing
 * around it. The mapping is angular, not linear degrees-per-pixel: the
 * rotation angle is the change in the cursor's bearing about the pivot
 * (the plane intersection point, projected to canvas coordinates), so the
 * anatomy tracks the cursor 1:1 rather than at a fixed rate. See
 * {@link signedAngleAboutPivotRad} and {@link rotationAboutViewNormal} for
 * the mapping and its sign convention.
 *
 * The drag angle is always computed from the press position and composed
 * onto the rotation captured at press time — never accumulated from the
 * previous move event — so the same physical drag produces the same
 * matrix regardless of how many intermediate move events the browser
 * happens to fire. That is a determinism requirement, not a style
 * preference: incremental accumulation would make the result depend on
 * event sampling rate.
 *
 * The pivot and view normal are both captured once, at press time, from
 * the view the drag started on — never re-read from whichever view a
 * later move event happens to report through (the pointer can stray onto
 * a neighbouring view's canvas mid-drag; only the cursor should follow
 * it, not the rotation math). The plane intersection point itself needs
 * no explicit handling beyond that snapshot: each {@link PlaneView}
 * re-derives its own position from the shared intersection point whenever
 * its orientation changes, so the planes swing about the crosshair rather
 * than the view centre.
 *
 * The tool does not own the rotation state: it reads the current value
 * through `getFrameRotation` and reports the composed result through
 * `onFrameRotationChanged`, exactly as `BrushTool` reports its size
 * through a callback rather than owning it. Both calls name the press
 * view, so the owner can resolve which frame the drag belongs to.
 */
export class RotateViewTool extends Tool {
    public readonly toolType = 'rotateView';

    private readonly _getFrameRotation: (view: PlaneView) => ReadonlyMat3;
    private readonly _onFrameRotationChanged: (
        rotation: ReadonlyMat3,
        view: PlaneView
    ) => void;

    /**
     * The view the drag started on. Every move's coordinates are
     * converted to canvas space, and the pivot looked up, through *this*
     * view — never through whichever view the move event itself reports
     * through — so a pointer straying onto a neighbouring view's canvas
     * mid-drag cannot corrupt the rotation math (see class doc).
     */
    private _pressView: PlaneView | null = null;
    /** Press position, in the press view's canvas-pixel coordinates. */
    private _pressCanvasPx: ReadonlyVec2 | null = null;
    /**
     * The pivot (plane intersection point, projected to the press view's
     * canvas coordinates) at press time.
     */
    private _pivotCanvasPx: ReadonlyVec2 | null = null;
    /**
     * Frame rotation at press time. Every move composes the drag delta
     * onto this fixed baseline, never onto the previous move's result.
     */
    private _pressRotation: ReadonlyMat3 | null = null;
    /** The press view's normal (PatientSpace), captured at press time. */
    private _viewNormal: ReadonlyVec3 | null = null;

    public constructor(
        getFrameRotation: (view: PlaneView) => ReadonlyMat3,
        onFrameRotationChanged: (
            rotation: ReadonlyMat3,
            view: PlaneView
        ) => void
    ) {
        super();
        this._getFrameRotation = getFrameRotation;
        this._onFrameRotationChanged = onFrameRotationChanged;
    }

    public onMousePress(
        button: number,
        _modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: TextureView
    ): EventHandlerResult {
        if (button === 0 && view instanceof PlaneView) {
            const pressCanvasPx = view.clientToCanvas(coordinates);
            const pivotCanvasPx = view.canvasCoordsFromPatientCoords(
                view.planeIntersectionPoint.coordinates
            );

            if (
                vec2.distance(pressCanvasPx, pivotCanvasPx) <
                ROTATE_DEAD_ZONE_PX
            ) {
                // Degenerate at the centre — see class doc and
                // ROTATE_DEAD_ZONE_PX: do not start a drag.
                return new EventHandlerResult(
                    EventHandlerResultType.EVENT_NOT_HANDLED
                );
            }

            this._pressView = view;
            this._pressCanvasPx = pressCanvasPx;
            this._pivotCanvasPx = pivotCanvasPx;
            this._pressRotation = this._getFrameRotation(view);
            this._viewNormal = view.viewDirection;

            return new EventHandlerResult(
                EventHandlerResultType.EVENT_HANDLED,
                true
            );
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMouseMove(
        coordinates: ReadonlyVec2,
        _buttons: number,
        _view: TextureView
    ): EventHandlerResult {
        if (
            !this._pressView ||
            !this._pressCanvasPx ||
            !this._pivotCanvasPx ||
            !this._pressRotation ||
            !this._viewNormal
        ) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        // Converted through the PRESS view, not `view` — see class doc:
        // the pointer may have strayed onto a different view's canvas,
        // whose own client-to-canvas offset and pivot must not leak into
        // this view's rotation math.
        const currentCanvasPx = this._pressView.clientToCanvas(coordinates);

        // Bearing about the pivot, from the PRESS position, not the
        // previous move — see the class doc. This is what keeps the
        // result independent of how many move events fired between press
        // and here.
        const angleRad = signedAngleAboutPivotRad(
            this._pressCanvasPx,
            currentCanvasPx,
            this._pivotCanvasPx
        );

        // No angular movement yet: nothing to report. Also avoids
        // re-reporting an unchanged rotation on duplicate move events at
        // the same point.
        if (angleRad === 0) {
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }

        const delta = rotationAboutViewNormal(angleRad, this._viewNormal);
        const composed = composeFrameRotation(this._pressRotation, delta);
        this._onFrameRotationChanged(composed, this._pressView);

        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    public onMouseRelease(
        _button: number,
        _coordinates: ReadonlyVec2,
        _view: TextureView
    ): EventHandlerResult {
        if (!this._pressCanvasPx) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }

        this._pressView = null;
        this._pressCanvasPx = null;
        this._pivotCanvasPx = null;
        this._pressRotation = null;
        this._viewNormal = null;

        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    /**
     * Ends any in-progress drag. Called by {@link ToolManager} when the
     * user switches to a different tool, so a tool switch mid-drag cannot
     * leave a phantom drag active.
     */
    public override onDeactivate(): EventHandlerResult {
        this._pressView = null;
        this._pressCanvasPx = null;
        this._pivotCanvasPx = null;
        this._pressRotation = null;
        this._viewNormal = null;

        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    public override isActionInProgress(): boolean {
        return this._pressCanvasPx !== null;
    }

    /** 'grabbing' while a drag is in progress, 'grab' otherwise. */
    public override get cursor(): string {
        return this._pressCanvasPx ? 'grabbing' : 'grab';
    }
}
